import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { School } from '../types'
import {
  CreateSchoolRequest,
  getSchools,
  createSchool,
  updateSchool,
  deleteSchool,
} from '../services/schools'

interface SchoolState {
  // State
  schools: School[]
  loading: boolean
  error: string | null
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean

  // Actions
  fetchSchools: () => Promise<void>
  addSchool: (schoolData: CreateSchoolRequest) => Promise<School>
  editSchool: (
    id: string,
    schoolData: Partial<CreateSchoolRequest>,
  ) => Promise<School>
  removeSchool: (id: string) => Promise<void>
  clearError: () => void
  setLoading: (loading: boolean) => void
}

export const useSchoolStore = create<SchoolState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        schools: [],
        loading: false,
        error: null,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,

        fetchSchools: async () => {
          try {
            set({ loading: true, error: null })
            const schools = await getSchools()
            set({
              schools,
              loading: false,
            })
          } catch (error: any) {
            console.error('Error fetching schools:', error)
            set({
              error: error.response.data.error || 'Failed to fetch schools',
              loading: false,
            })
          }
        },

        addSchool: async (schoolData: CreateSchoolRequest) => {
          try {
            set({ isCreating: true, error: null })
            const newSchool = await createSchool(schoolData)

            // Add the new school to the store
            set((state) => ({
              schools: [...state.schools, newSchool],
              isCreating: false,
            }))
            return newSchool
          } catch (error: any) {
            console.error('Error creating school:', error)
            set({
              error: error.response.data.error || 'Failed to create school',
              isCreating: false,
            })
            throw error
          }
        },

        editSchool: async (
          id: string,
          schoolData: Partial<CreateSchoolRequest>,
        ) => {
          try {
            set({ isUpdating: true, error: null })
            const updatedSchool = await updateSchool(id, schoolData)

            // Update the school in the store
            set((state) => ({
              schools: state.schools.map((school) =>
                school._id === id ? updatedSchool : school,
              ),
              isUpdating: false,
            }))
            return updatedSchool
          } catch (error: any) {
            console.error('Error updating school:', error)
            set({
              error: error.response.data.error || 'Failed to update school',
              isUpdating: false,
            })
            throw error
          }
        },

        removeSchool: async (id: string) => {
          try {
            set({ isDeleting: true, error: null })
            await deleteSchool(id)

            // Remove the school from the store
            set((state) => ({
              schools: state.schools.filter((school) => school._id !== id),
              isDeleting: false,
            }))
          } catch (error: any) {
            console.error('Error deleting school:', error)
            set({
              error: error.response.data.error || 'Failed to delete school',
              isDeleting: false,
            })
            throw error
          }
        },

        clearError: () => set({ error: null }),
        setLoading: (loading: boolean) => set({ loading }),
      }),
      {
        name: 'school-store',
      },
    ),
  ),
)
